import CustomDropdown from '../reusable/dropdown';
import CustomInput from '../reusable/input';
import { emailAndPhoneOptions } from '../../helper/index';
import { gray } from '../../theme/themePrimitives';
import LabelWrapper from '../wrapper/label';
import { LeadObject } from '../../params/redux/leads';
import { messageComposeSchema } from '../../schema/email_schema';
import { SendBulkEmailsParams } from '../../params/redux/email';
import { sendBulkEmailsThunk } from '../../redux/thunks/email';
import { settingBulkMessageFlag } from '../../redux/slices/leads';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import { Bounce, toast } from 'react-toastify';
import { Box, Button, Chip, Typography } from '@mui/material';
import { Close, KeyboardArrowLeft } from '@mui/icons-material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';

interface QuillEditorRef {
  insertTextAtCursor: (text: string) => void;
}


const ComposeBulkEmailComponent = () => {
    const { id } = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const quillEditorRef = useRef<QuillEditorRef>(null);
    const { leads, bulkMessage } = useSelector((state: RootState) => state.lead);
    const { loaders } = useSelector((state: RootState) => state.email);
    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    useEffect(() => {
        if (bulkMessage.selectedRows.length) {
            const selectedleads: LeadObject[] = leads.filter((lead: LeadObject) => bulkMessage.selectedRows.includes(lead.id));
            const emails = selectedleads.map((lead: LeadObject) => lead.email);
            const ids = selectedleads.map((lead: LeadObject) => lead.id);
            setSelectedEmails(emails as string[]);
            setSelectedIds(ids);
        }
    }, []);

    const handleRemoveEmail = (email: string) => {
        setSelectedEmails(selectedEmails.filter((e) => e !== email));
    };


    const navigateBackHandler = () => {
        dispatch(settingBulkMessageFlag({
            type: null,
            rows: []
        }));
    };

    const submitEmail = async (values: { subject: string; body: string }) => {
        try {
            const data: SendBulkEmailsParams = {
                data: selectedIds,
                subject: values.subject,
                body: values.body,
                client_id: id ? parseInt(id as string, 10) : 0
            };
            await dispatch(sendBulkEmailsThunk(data)).unwrap();
            toast.success('Email sent successfully!', {
                theme: 'dark',
                position: 'top-left',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                transition: Bounce
            });
            navigateBackHandler();
        } catch (e) {
            //
        }
    };

    return (
        <Formik
            initialValues={{ subject: '', body: '' }}
            validationSchema={messageComposeSchema}
            onSubmit={submitEmail}
        >
            {({ values, setFieldValue, handleChange, handleBlur, errors, touched }) => (
                <Form>
                <Box display="flex" flexDirection="column" gap={2}>
                    {/* Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Button variant="text" color="secondary" startIcon={<KeyboardArrowLeft />} disabled={loaders.isCreating} onClick={navigateBackHandler}>
                            Back
                        </Button>
                        <Typography variant="h4" color="textSecondary">Compose mail</Typography>
                        <Box></Box>
                    </Box>

                    {/* Email Input with Chips */}
                    <Box>
                        <LabelWrapper label="To:" />
                        <Box
                            p={1}
                            border={`1px solid ${gray[300]}`}
                            borderRadius={1}
                            display="flex"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={1}
                        >
                            {selectedEmails.length === 0
                                ? (<Typography color="gray">No emails selected</Typography>)
                                : (
                                    selectedEmails.map((email) => (
                                        <Chip
                                            key={email}
                                            label={email}
                                            onDelete={() => handleRemoveEmail(email)}
                                            deleteIcon={<Close />}
                                            color="primary"
                                            variant="outlined"
                                            sx={{
                                                padding: '15px 0',
                                                borderRadius: '5px'
                                            }}
                                        />
                                    ))
                                )}
                        </Box>
                    </Box>

                    {/* Subject Field */}
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="end" mb={1}>
                            <LabelWrapper label="Subject:" />
                            <Box>
                                <CustomDropdown
                                    options={emailAndPhoneOptions}
                                    value=""
                                    placeholder="Add Field"
                                    disabled={loaders.isCreating}
                                    onChange={(e) => setFieldValue('subject', `${values.subject} [${e.target.value}]`)}
                                />
                            </Box>
                        </Box>
                        <CustomInput
                            name="subject"
                            placeholder="Email Subject"
                            fullWidth
                            variant="outlined"
                            value={values.subject}
                            onChange={handleChange('subject')}
                            onBlur={handleBlur('subject')}
                            disabled={loaders.isCreating}
                        />
                    </Box>

                    {/* Message Box */}
                    <Box width="100%" height="30vh" >
                        <Box display="flex" justifyContent="space-between" alignItems="end" mb={1}>
                            <LabelWrapper label="Message:" />
                            <Box>
                                <CustomDropdown
                                    options={emailAndPhoneOptions}
                                    value=""
                                    placeholder="Add Field"
                                    // onChange={(e) => setFieldValue('body', `${values.body} [${e.target.value}]`)}
                                    onChange={(e) => {
                                            const textToInsert = ` [${e.target.value}] `;
                                            quillEditorRef.current?.insertTextAtCursor?.(textToInsert);
                                        }}
                                    disabled={loaders.isCreating}
                                />
                            </Box>
                        </Box>
                        <CustomInput
                            type="quill"
                            value={values.body}
                            name="content"
                            quillEditorRef={quillEditorRef}
                            placeholder="Type content here..."
                            quillHeight="22vh"
                            disabled={loaders.isCreating}
                            error={Boolean(errors.body && touched.body)}
                            onChangeForPhone={(value: string) => setFieldValue('body', value)}
                        />
                        {/* <CustomToolbar />
                        <ReactQuill
                            onChange={(content) => }
                            value={values.content}
                            placeholder="Type content here..."
                            modules={quillModule}
                            formats={quillFormats}
                            theme="snow"
                            readOnly={loaders.isCreating}
                            style={{
                                height: '22vh',
                                borderBottomLeftRadius: '10px',
                                borderBottomRightRadius: '10px',
                                border:  ? `1px solid ${red[300]}` : 'none'
                            }}
                        /> */}
                    </Box>

                    {/* Submit Button */}
                    <Box display="flex" justifyContent="end" gap={2} >
                        <Button variant="outlined" color="secondary" size="large" disabled={loaders.isCreating} onClick={navigateBackHandler}>Cancel</Button>
                        <Button type="submit" variant="contained" color="primary" size="large" disabled={loaders.isCreating}>
                            {loaders.isCreating ? 'Sending...' : 'Send'}
                        </Button>
                    </Box>
                </Box>
            </Form>
            )}
        </Formik>
    );
};

export default ComposeBulkEmailComponent;